import { presetRange } from "@/lib/dateRange";
import type { Period } from "@/components/PeriodTabs";
import type { KpiEntry } from "@/api/kpi";

/** Kuyruk KPI donemi: eski `#periodSel` (today | 7d | 30d | all) + gidisat gunleri. */
export type KpiPeriod = Period | "today" | "all";

import { healthBreakdown, healthScore, type KpiLike } from "./health";

/**
 * PERIOD_FILTERED_KPIS — legacy `frontend/public/js/app.js` port
 * (docs/faz5-arayuz-spec.md §5). `/api/v2/kpi/snapshot`'in `kpis[id].items`
 * dizisi tarih alanina gore kirpilir, ozet sayilar (count/critical_count/
 * warning_count/alarm_level) YENIDEN hesaplanir; tarihsiz KPI'lar (kritik_stok)
 * donemden ETKILENMEZ. Sonrasinda destek_iade_talepleri aggregate'i ve saglik
 * skoru/dokum, kirpilmis KPI'lardan YENIDEN uretilir.
 */
export const PERIOD_FILTERED_KPIS: Record<string, string> = {
  kargolanmamis: "created_at",
  kargo_yolda: "created_at",
  destek_talepleri: "created_at",
  bekleyen_iade: "request_date",
  odeme_bekleyen: "created_at",
  bugunku_siparis: "created_at",
  bugun_destek: "created_at",
};

function isItem(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

/** `null` → filtre yok (Tüm Zamanlar); "today" → yerel gun baslangici. */
function periodCutoff(period: KpiPeriod, now: Date): Date | null {
  if (period === "all") return null;
  const range = presetRange(period === "today" ? "today" : String(period) as "7" | "30" | "90", now);
  return new Date(`${range.start}T00:00:00+03:00`);
}

/** Cards, detail tables and their AI inputs must use the same queue scope. */
export function filterKpiRows(kpiId: string, rows: KpiEntry[], period: KpiPeriod, now: Date = new Date()): KpiEntry[] {
  const dateField = PERIOD_FILTERED_KPIS[kpiId];
  const cutoff = periodCutoff(period, now);
  if (!dateField || cutoff === null) return rows;
  return rows.filter(row => {
    const raw = row[dateField] ?? row.created_at;
    if (!raw) return false;
    const date = new Date(String(raw));
    return !Number.isNaN(date.getTime()) && date >= cutoff;
  });
}

/** Use the card's snapshot, enriching only deep links from the newer detail response.
 * Mixing a newer queue with an older card would produce different counts even
 * with an identical date filter. Missing snapshots fall back to filtered API rows.
 */
export function queueDetailRows(kpiId: string, rows: KpiEntry[], snapshot: Record<string, unknown> | null | undefined, period: KpiPeriod): KpiEntry[] {
  const entry = snapshot?.[kpiId];
  if (!PERIOD_FILTERED_KPIS[kpiId] || !isItem(entry) || !Array.isArray(entry.items)) return filterKpiRows(kpiId, rows, period);
  const identity = (row: KpiEntry) => String(row.order_id ?? row.order_no ?? row.ticket_id ?? row.return_id ?? "");
  const links = new Map(rows.filter(row => identity(row)).map(row => [identity(row), row.admin_link]));
  return filterKpiRows(kpiId, entry.items as KpiEntry[], period).map(row => {
    const link = links.get(identity(row));
    return typeof link === "string" ? { ...row, admin_link: link } : row;
  });
}

/**
 * Filtrelenmis item'larin `amount` alanlarini toplar — backend
 * `calculator.py` `kpi_bugunku_siparis`/`kpi_odeme_bekleyen`'in
 * `sum(float(it.get("amount",0) or 0) for it in items)` hesabiyla AYNI
 * yontem (satir 503/536). Gorev C icin kullanilir.
 */
function sumAmount(items: Record<string, unknown>[]): number {
  let total = 0;
  for (const it of items) {
    const v = Number(it.amount);
    if (Number.isFinite(v)) total += v;
  }
  return Math.round(total * 100) / 100;
}

function recomputeKpiSummary(items: Record<string, unknown>[], original: KpiEntry): KpiEntry {
  let critical = 0;
  let warning = 0;
  let ok = 0;
  let unknown = 0;
  for (const it of items) {
    const level = it.level;
    if (level === "critical") critical++;
    else if (level === "warning") warning++;
    else if (level === "unknown") unknown++;
    else ok++;
  }
  const next: KpiEntry = {
    ...original,
    items,
    count: items.length,
    critical_count: critical,
    warning_count: warning,
    ok_count: ok,
    unknown_count: unknown,
    alarm_level: critical > 0 ? "critical" : warning > 0 ? "warning" : "ok",
  };
  // Gorev C: "Bugun" seridindeki tutarlar da donemle kucalsin. Orijinal
  // KPI'da bu alanlardan biri VARSA (bugunku_siparis.today_revenue,
  // odeme_bekleyen.total_amount; total_revenue baska bir KPI'da olabilir
  // diye genel tutuldu) kirpilmis item'larin `amount` toplamiyla YENIDEN
  // yazilir.
  for (const field of ["today_revenue", "total_amount", "total_revenue"] as const) {
    if (typeof original[field] === "number") next[field] = sumAmount(items);
  }
  return next;
}

interface KargoSirketiGrup {
  company: string;
  total: number;
  critical: number;
  warning: number;
}

/**
 * `kargo_yolda.items`'tan kargo şirketi bazında gruplama — eski
 * `frontend/legacy/js/app.js:243-251` portu. Alan adları
 * `backend/app/kpi/calculator.py kpi_kargo_yolda`'nın `by_cargo_company`
 * çıktısıyla BİREBİR aynı (`company`/`total`/`critical`/`warning`) — item'daki
 * şirket alanı `cargo_company`, seviye alanı `level` (calculator.py:148,157).
 */
function groupByCargoCompany(items: Record<string, unknown>[]): KargoSirketiGrup[] {
  const groups = new Map<string, KargoSirketiGrup>();
  for (const it of items) {
    const raw = typeof it.cargo_company === "string" ? it.cargo_company.trim() : "";
    const company = raw || "Belirsiz";
    const g = groups.get(company) ?? { company, total: 0, critical: 0, warning: 0 };
    g.total += 1;
    if (it.level === "critical") g.critical++;
    else if (it.level === "warning") g.warning++;
    groups.set(company, g);
  }
  return Array.from(groups.values());
}

/**
 * `kargo_sirketi` KPI'sını FİLTRELENMİŞ `kargo_yolda.items`'tan yeniden
 * üretir — eski `app.js:259-266` portu (item 2/B). Yalnız `by_cargo_company`
 * ve `count` (= şirket sayısı, backend `kpi_kargo_sirketi`nin shipping_items
 * boşken ürettiği anlamla TUTARLI) üzerine yazılır; diğer alanlar (`items`,
 * `alarm_level` vb.) dokunulmadan kalır. `kargo_sirketi` kartı yoksa (capability
 * kapalı) hiçbir şey yapılmaz.
 */
function recomputeKargoSirketi(
  kargoYoldaItems: Record<string, unknown>[],
  original: KpiEntry | undefined,
): KpiEntry | undefined {
  if (!isItem(original)) return original;
  const byCargoCompany = groupByCargoCompany(kargoYoldaItems);
  return {
    ...original,
    by_cargo_company: byCargoCompany,
    count: byCargoCompany.length,
  };
}

function buildDestekIadeAggregate(destek: KpiEntry | undefined, iade: KpiEntry | undefined): KpiEntry {
  const d = (destek ?? { count: 0, critical_count: 0, warning_count: 0 }) as Record<string, unknown>;
  const i = (iade ?? { count: 0, critical_count: 0, warning_count: 0 }) as Record<string, unknown>;
  const count = (Number(d.count) || 0) + (Number(i.count) || 0);
  const crit = (Number(d.critical_count) || 0) + (Number(i.critical_count) || 0);
  const warn = (Number(d.warning_count) || 0) + (Number(i.warning_count) || 0);
  return {
    id: "destek_iade_talepleri",
    count,
    critical_count: crit,
    warning_count: warn,
    ok_count: Math.max(0, count - crit - warn),
    alarm_level: crit > 0 ? "critical" : warn > 0 ? "warning" : "ok",
  };
}

export interface PeriodFilteredResult {
  kpis: Record<string, KpiEntry>;
  healthScore: number | null;
  healthBreakdown: ReturnType<typeof healthBreakdown>;
}

/**
 * `kpis` (snapshot.kpis) icin donem filtresi uygular; saglik skoru/dokumunu
 * ayni gecisten YENIDEN hesaplar (backend health_score.py formulu ile
 * tutarli olmasi icin `./health` kullanilir — legacy JS'in kendi
 * computeHealthScore'u DEGIL).
 */
export function applyPeriodFilter(
  kpis: Record<string, unknown> | null | undefined,
  period: KpiPeriod,
  now: Date = new Date(),
): PeriodFilteredResult {
  const source = kpis ?? {};
  const next: Record<string, KpiEntry> = { ...(source as Record<string, KpiEntry>) };

  for (const kpiId of Object.keys(PERIOD_FILTERED_KPIS)) {
    const kpi = next[kpiId];
    if (!isItem(kpi) || !Array.isArray(kpi.items)) continue;
    const filtered = filterKpiRows(kpiId, kpi.items as KpiEntry[], period, now);
    next[kpiId] = recomputeKpiSummary(filtered, kpi);
  }

  next.destek_iade_talepleri = buildDestekIadeAggregate(next.destek_talepleri, next.bekleyen_iade);

  // Görev B: kargo şirketi dağılımı kuyruğu dönem filtresine uysun — yukarıdaki
  // döngüde ZATEN kırpılmış `kargo_yolda.items`'tan yeniden gruplanır.
  const kargoYoldaItems =
    isItem(next.kargo_yolda) && Array.isArray(next.kargo_yolda.items)
      ? (next.kargo_yolda.items as Record<string, unknown>[])
      : [];
  const recomputedKargoSirketi = recomputeKargoSirketi(kargoYoldaItems, next.kargo_sirketi);
  if (recomputedKargoSirketi) next.kargo_sirketi = recomputedKargoSirketi;

  const kpisAsHealth = next as unknown as Record<string, KpiLike | null | undefined>;
  return {
    kpis: next,
    healthScore: healthScore(kpisAsHealth),
    healthBreakdown: healthBreakdown(kpisAsHealth),
  };
}
